<script lang="ts">
import { computed, defineComponent, type PropType } from 'vue';
import type { UICardDisplay, VillainEffectKeyword } from '@legendary-arena/game-engine';
import CrossedSwordsIcon from './CrossedSwordsIcon.vue';
import {
  eventCardId,
  type NotableGameEvent,
} from '../../composables/useNotableEventStream';

/**
 * Lookup contract for resolving an event's card ext_id to its display
 * name. Keyed by ext_id, values are the engine-projected UICardDisplay.
 *
 * The spec wording in WP-201 §Scope (In) references
 * `UIState.cardDisplayData` — that field does not exist as a top-level
 * UIState projection (per pre-flight inspection of `uiState.types.ts`
 * on `main @ 52d64e2`; `cardDisplayData` lives on G and surfaces only
 * through per-zone embedded `display` fields). The consumer
 * (PlayDesktop) constructs this map from the snapshot's visible zones
 * and passes it in. When a card is not in the map (e.g., a freshly-
 * defeated villain that has already moved to the active player's
 * victory pile), the overlay falls back to the raw ext_id per the
 * locked fallback rule.
 */
export type NotableEventCardLookup = Readonly<Record<string, UICardDisplay>>;

/**
 * Descriptive notable-event overlay surfacing the engine-composed
 * `NotableGameEvent` stream from WP-200. Renders a chip + card name +
 * engine-composed narrative + applied-effect badges for whichever
 * `NotableGameEventType` variant the current event carries. The per-variant
 * chip label + accent live in the `CHIP_LABELS` map + the per-`data-event-type`
 * CSS blocks below; the engine's `NOTABLE_EVENT_TYPES` array is the source of
 * truth for the full variant set (eleven as of WP-697 / `heroEffectResolved`),
 * so this comment intentionally does not re-enumerate them (it drifted from
 * "five" once the set grew past the WP-200/D-20008 originals).
 *
 * Per D-20002 the narrative is rendered verbatim (engine-authoritative
 * composition). Per D-20105 the UI does not interpret event semantics —
 * the only permitted branching is per-event-type styling (border + chip
 * label) and presence/absence of the `appliedEffects` badge row.
 *
 * Per D-20104 the overlay reaches the discriminator-appropriate ext_id
 * solely through the `eventCardId(event)` pure helper exported from the
 * composable module — no inline per-variant ternaries over the per-variant
 * id fields. Grep gate in EC-228 enforces this contract literally.
 *
 * Per the EC-132 §2 SFC authoring whitelist: this component is a tested
 * non-leaf composer (Pinia-adjacent via the consuming page, has computed
 * state, renders child markup that needs binding via _ctx) so it MUST
 * use `defineComponent({ setup() { return {...} } })` per D-6512.
 */

// why: locked humanised effect-label map (D-20102). D-24646 types it as a Record
// over the engine's VillainEffectKeyword union, so a keyword added to the engine
// without a label here fails vue-tsc instead of reaching a player as its raw
// camelCase name (koHeroEachPlayerMag2 did, on Juggernaut's Ambush). The raw
// fallback in effectLabel remains for a deploy skew (an engine ahead of this bundle).
const EFFECT_LABELS: Readonly<Record<VillainEffectKeyword, string>> = {
  gainWoundEachPlayer: 'Each player gains a Wound',
  gainWoundCurrentPlayer: 'You gain a Wound',
  koHeroCurrentPlayer: 'KO a Hero',
  heroDeckTopToEscape: 'Hero deck top escapes',
  captureBystander: 'Captures a Bystander',
  koHeroEachPlayer: 'Each player KOs a Hero',
  koHeroEachPlayerMag2: 'Each player KOs two Heroes',
  captureHqHeroRightmost: 'Captures the rightmost HQ Hero',
  captureHqHeroHighestCost: 'Captures the highest-cost HQ Hero',
  captureHqHeroLowestCost: 'Captures the lowest-cost HQ Hero',
};

// why: D-24646 — villain-deck Bystanders (`bystander-villain-deck-NN`) and the supply
// Bystander (`pile-bystander`) are often absent from the overlay's board-built lookup
// (a Bystander captured by a city villain is not a board card), so name them generically
// rather than falling back to the ext_id.
const SUPPLY_BYSTANDER_EXT_ID = 'pile-bystander';
const VILLAIN_DECK_BYSTANDER_PREFIX = 'bystander-villain-deck-';

/**
 * Whether an ext_id is a Bystander card (supply or villain-deck copy).
 *
 * @param extId The card ext_id.
 * @returns True for a Bystander ext_id.
 */
function isBystanderExtId(extId: string): boolean {
  return extId === SUPPLY_BYSTANDER_EXT_ID || extId.startsWith(VILLAIN_DECK_BYSTANDER_PREFIX);
}

// why: locked chip labels — twelve entries matching `NotableGameEventType`
// exactly (D-20008 added `mastermindDefeated`; WP-381 / D-24182 added
// `healResolved`; WP-602 / D-24412 added `bystanderRevealed`; WP-642 / D-24454
// added `deckReshuffled`; WP-644 / D-24456 added `strikeBlocked`; WP-672 /
// D-24487 added `transformResolved`; WP-697 / D-24516 added `heroEffectResolved`;
// WP-746 / D-24569 added `excessiveViolenceFired`). The labels are user-facing
// English (engine-side type names use camelCase suffixes). `heroEffectResolved`
// is a general bucket for invisible-work hero effects (v1 the reveal-for-attack
// family), so its chip is the generic "Hero Ability" — future families reuse the
// type and differ only by narrative.
const CHIP_LABELS: Readonly<Record<string, string>> = {
  fightResolved: 'Fought',
  ambushResolved: 'Ambush!',
  schemeTwistResolved: 'Scheme Twist!',
  mastermindStrikeResolved: 'Master Strike!',
  mastermindDefeated: 'Mastermind Defeated!',
  healResolved: 'Healed',
  bystanderRevealed: 'Bystander!',
  deckReshuffled: 'Deck Shuffled',
  strikeBlocked: 'Blocked!',
  transformResolved: 'Transformed!',
  heroEffectResolved: 'Hero Ability',
  excessiveViolenceFired: 'Excessive Violence!',
};

function chipLabel(type: string): string {
  return CHIP_LABELS[type] ?? type;
}

function effectLabel(keyword: string): string {
  // why: totality fallback (D-20102) — raw keyword verbatim when a newer
  // engine emits a keyword this bundle predates (a deploy skew). Silent-skip
  // is forbidden; the raw string surfaces the data so operators see
  // "something new happened" rather than a swallowed effect.
  return EFFECT_LABELS[keyword as VillainEffectKeyword] ?? keyword;
}

export default defineComponent({
  name: 'NotableEventOverlay',
  components: { CrossedSwordsIcon },
  props: {
    event: {
      type: Object as PropType<NotableGameEvent | null>,
      default: null,
    },
    cardDisplayData: {
      type: Object as PropType<NotableEventCardLookup>,
      default: undefined,
    },
    durationMs: {
      type: Number,
      default: 2500,
    },
  },
  emits: ['dismiss'],
  setup(props) {
    const cardId = computed<string | null>(() => {
      if (props.event === null) return null;
      const id = eventCardId(props.event);
      // why: WP-381 — an empty id (healResolved has no card) means no card-name
      // row; map it to null so the overlay renders only the chip + narrative.
      return id === '' ? null : id;
    });

    const cardName = computed<string | null>(() => {
      const id = cardId.value;
      if (id === null) return null;
      // why: defensive fallback to the raw ext_id when display data is
      // absent. The engine emits events even when a card's display entry
      // is missing (D-20002 — narrative composition is robust to that
      // case); the overlay mirrors the same defensive posture rather than
      // throwing or hiding the event.
      const entry = props.cardDisplayData?.[id];
      if (entry !== undefined && typeof entry.name === 'string') {
        return entry.name;
      }
      if (isBystanderExtId(id)) {
        return 'Bystander';
      }
      return id;
    });

    /**
     * The revealed card's image URL for a `heroEffectResolved` reveal event, or
     * null when there is no id or no served image.
     */
    // why: WP-789 / D-24637 — only the reveal family shows a card image, and only
    // from served display data (D-20105); an absent id or URL keeps today's render.
    const cardImageUrl = computed<string | null>(() => {
      const event = props.event;
      const id = cardId.value;
      if (event === null || event.type !== 'heroEffectResolved' || id === null) {
        return null;
      }
      const imageUrl = props.cardDisplayData?.[id]?.imageUrl;
      if (typeof imageUrl !== 'string' || imageUrl === '') {
        return null;
      }
      return imageUrl;
    });

    const chipText = computed<string>(() => {
      if (props.event === null) return '';
      return chipLabel(props.event.type);
    });

    // why: D-20105 — branch SOLELY on per-event-type styling (chip + border
    // + appliedEffects presence). NEVER on event semantics. The Fight/Ambush
    // discriminator check below narrows the union to the two variants that
    // carry `appliedEffects` (D-20005 — Scheme Twist + Master Strike do not).
    const appliedEffects = computed<readonly string[]>(() => {
      const event = props.event;
      if (event === null) return [];
      if (event.type !== 'fightResolved' && event.type !== 'ambushResolved') {
        return [];
      }
      return event.appliedEffects;
    });

    const showEffectBadges = computed<boolean>(
      () => appliedEffects.value.length > 0,
    );

    const rootStyle = computed<Record<string, string>>(() => ({
      '--notable-event-overlay-duration-ms': `${props.durationMs}ms`,
    }));

    return {
      cardName,
      cardImageUrl,
      chipText,
      showEffectBadges,
      appliedEffects,
      rootStyle,
      effectLabel,
    };
  },
});
</script>

<template>
  <Transition name="notable-event-fade">
    <div
      v-if="event !== null"
      class="notable-event-overlay"
      data-testid="play-notable-event-overlay"
      :data-event-type="event.type"
      :style="rootStyle"
      aria-live="polite"
      role="status"
      aria-atomic="true"
    >
      <span class="notable-event-overlay__chip">
        <CrossedSwordsIcon
          v-if="event.type === 'excessiveViolenceFired'"
          class="notable-event-overlay__chip-icon"
        />
        {{ chipText }}
      </span>
      <img
        v-if="event.type === 'heroEffectResolved' && cardImageUrl !== null"
        class="notable-event-overlay__card-image"
        data-testid="play-notable-event-overlay-card-image"
        :src="cardImageUrl"
        :alt="cardName ?? ''"
      />
      <p class="notable-event-overlay__card-name">{{ cardName }}</p>
      <p class="notable-event-overlay__narrative">{{ event.narrative }}</p>
      <ul
        v-if="showEffectBadges"
        class="notable-event-overlay__effects"
        data-testid="play-notable-event-overlay-effects"
      >
        <li
          v-for="(keyword, index) in appliedEffects"
          :key="`${keyword}-${index}`"
          class="notable-event-overlay__effect-badge"
          :data-effect-keyword="keyword"
        >
          {{ effectLabel(keyword) }}
        </li>
      </ul>
    </div>
  </Transition>
</template>

<style scoped>
.notable-event-overlay {
  position: fixed;
  top: 40%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 200;
  padding: 1rem 1.5rem;
  border-radius: 0.5rem;
  text-align: center;
  pointer-events: none;
  background: var(--color-notable-event-bg, rgba(0, 0, 0, 0.85));
  color: var(--color-notable-event-fg, #fff);
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.4);
  border: 2px solid transparent;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.4rem;
  max-width: 28rem;
}

/* why: WP-789 — the revealed card's face, kept small so the overlay stays compact. */
.notable-event-overlay__card-image {
  max-height: 12rem;
  width: auto;
  border-radius: 0.35rem;
}

.notable-event-overlay[data-event-type="fightResolved"],
.notable-event-overlay[data-event-type="ambushResolved"] {
  border-color: var(--color-villain, #7b1fa2);
}

.notable-event-overlay[data-event-type="schemeTwistResolved"] {
  border-color: var(--color-scheme-twist, #e6a817);
}

.notable-event-overlay[data-event-type="mastermindStrikeResolved"] {
  border-color: var(--color-master-strike, #c62828);
}

.notable-event-overlay[data-event-type="mastermindDefeated"] {
  border-color: var(--color-victory, #2e7d32);
}

/* why: WP-381 — a distinct restorative teal for the Heal overlay, separate from
   the mastermind-defeat victory green. */
.notable-event-overlay[data-event-type="healResolved"] {
  border-color: var(--color-heal, #2e9e8f);
}

/* why: WP-602 — a friendly civilian blue for the Bystander reveal overlay,
   distinct from the gold scheme-twist / red master-strike / teal heal accents. */
.notable-event-overlay[data-event-type="bystanderRevealed"] {
  border-color: var(--color-bystander, #4a90d9);
}

/* why: WP-642 — a calm indigo for the deck-reshuffle overlay, distinct from the
   civilian-blue bystander / gold twist / red strike / teal heal accents. */
.notable-event-overlay[data-event-type="deckReshuffled"] {
  border-color: var(--color-deck-shuffle, #6d78d9);
}

/* why: WP-644 — a Captain-America blue for the strike-blocked overlay, distinct
   from the indigo reshuffle / civilian-blue bystander / gold twist / red strike
   / teal heal accents. */
.notable-event-overlay[data-event-type="strikeBlocked"] {
  border-color: var(--color-strike-blocked, #3f7fe0);
}

/* why: WP-672 — a gamma green for the transform overlay (the hero powering up),
   distinct from the Cap-blue block / indigo reshuffle / civilian-blue bystander /
   gold twist / red strike / teal heal accents. Matches the transform VFX surge. */
.notable-event-overlay[data-event-type="transformResolved"] {
  border-color: var(--color-transform, #5ee66b);
}

/* why: WP-697 — a warm hero amber for the invisible-work hero-effect overlay (the
   player's own ability paying off), distinct from the gamma-green transform / the
   scheme-twist gold (#e6a817) / the teal heal / the civilian-blue bystander. */
.notable-event-overlay[data-event-type="heroEffectResolved"] {
  border-color: var(--color-hero-ability, #f5a623);
}

/* why: WP-746 — a crimson for the Excessive Violence fire overlay (the fight
   overspend unleashed), distinct from the hero amber / gamma-green transform /
   Cap-blue block / gold twist / teal heal accents. Matches the crimson lead of
   the crossed-swords slash-burst VFX (#b3122b). */
.notable-event-overlay[data-event-type="excessiveViolenceFired"] {
  border-color: var(--color-excessive-violence, #b3122b);
}

.notable-event-overlay__chip {
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  padding: 0.1rem 0.6rem;
  border-radius: 999px;
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  background: rgba(255, 255, 255, 0.12);
}

/* why: WP-746 fix-forward — the crossed-swords icon on the Excessive Violence
   chip (same motif as the fight button + the blade-particle burst), so the beat
   reads as violence at a glance, not a bare "Excessive Violence!" text label
   (operator feedback 2026-09-23). Tinted to the crimson accent. */
.notable-event-overlay__chip-icon {
  font-size: 0.9rem;
  color: var(--color-excessive-violence, #b3122b);
}

.notable-event-overlay__card-name {
  margin: 0;
  font-size: 1.25rem;
  font-weight: 700;
}

.notable-event-overlay__narrative {
  margin: 0;
  font-size: 0.95rem;
  line-height: 1.35;
  opacity: 0.92;
}

.notable-event-overlay__effects {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-wrap: wrap;
  gap: 0.3rem;
  justify-content: center;
}

.notable-event-overlay__effect-badge {
  padding: 0.15rem 0.5rem;
  border-radius: 0.25rem;
  font-size: 0.75rem;
  font-weight: 600;
  background: rgba(255, 255, 255, 0.16);
}

.notable-event-fade-enter-active {
  transition: opacity 0.2s ease-out, transform 0.2s ease-out;
}

.notable-event-fade-leave-active {
  transition: opacity 0.4s ease-in;
}

.notable-event-fade-enter-from {
  opacity: 0;
  transform: translate(-50%, -50%) scale(0.9);
}

.notable-event-fade-leave-to {
  opacity: 0;
}
</style>
